import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Users, Info } from 'lucide-react';
import { Modal } from '../Modal';
import { ToggleSwitch } from '../ToggleSwitch';
import { useToast } from '../../contexts/ToastContext';
import { ApiError } from '../../services/apiClient';
import { CalendarEditorSeed, CalendarEventBody, calendarApi, closeEditor, useCalendarRange } from '../../services/calendarService';
import { useAffiliateDirectory } from '../../hooks/useAffiliateUserSearch';
import { CalendarRepeatFreq, CalendarScope, User, handleAvatarError } from '../../types';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import {
  DAY_MS, REMIND_OPTIONS_ALL_DAY, REMIND_OPTIONS_TIMED, WEEKDAY_KO, dateKeyToMs, isValidDateKey,
  keyParts, kstDateKey, kstIso, monthGridRange, nextHourMs, todayKey, toTimeInput,
} from '../../utils/calendarDate';
import { SCOPE_STYLE } from './scopeStyles';

interface EventEditorModalProps {
  seed: CalendarEditorSeed & { seq: number };
  currentUser: User;
}

const LIMIT = { title: 100, location: 100, description: 2000, attendees: 50 };
const MINUTE = 60 * 1000;

function initialTimes(seed: CalendarEditorSeed) {
  const ev = seed.event;
  if (ev) {
    return {
      allDay: ev.allDay,
      startDate: ev.startDate,
      endDate: ev.endDate,
      startTime: ev.allDay ? '09:00' : toTimeInput(ev.start),
      endTime: ev.allDay ? '10:00' : toTimeInput(ev.end),
    };
  }
  const today = todayKey();
  const date = seed.date && isValidDateKey(seed.date) ? seed.date : today;
  // 오늘이면 다음 정시, 다른 날이면 오전 9시부터 1시간
  const startMs = date === today ? nextHourMs() : dateKeyToMs(date) + 9 * 60 * MINUTE;
  const endMs = startMs + 60 * MINUTE;
  return {
    allDay: false,
    startDate: kstDateKey(startMs),
    endDate: kstDateKey(endMs),
    startTime: toTimeInput(startMs),
    endTime: toTimeInput(endMs),
  };
}

function toMsOf(dateKey: string, hhmm: string) {
  return new Date(kstIso(dateKey, hhmm)).getTime();
}

const inputClass = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-base md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500';
const labelClass = 'block text-[13px] font-semibold text-slate-600 mb-1';

/**
 * 일정 등록·수정 창 (CalendarModalsHost가 App 루트에서만 렌더한다).
 *
 * - 종류(내 일정/부서/회사)는 서버가 알려 준 등록 권한(GET /events의 canCreate)만 보여 준다 — 최종 판정은 서버.
 *   수정 때는 종류를 바꿀 수 없다(서버도 scope를 무시한다).
 * - 시간은 KST 고정(calendarDate) — 기기 시간대가 달라도 '+09:00' ISO로 보낸다. 시작을 옮기면 길이를 유지한 채 끝도 옮긴다.
 * - 참석자는 개인 일정만. 같은 계열사 명부(useAffiliateDirectory — 동료 검색과 같은 모듈 캐시)에서 고른다.
 * - 회사 일정에는 알림 항목이 없다(서버도 NULL로 강제 — 전 직원 푸시는 과하다).
 */
export const EventEditorModal: React.FC<EventEditorModalProps> = ({ seed, currentUser }) => {
  const { showToast } = useToast();
  const editing = seed.event || null;
  const init = useMemo(() => initialTimes(seed), []); // eslint-disable-line

  const thisMonth = useMemo(() => {
    const p = keyParts(todayKey());
    return monthGridRange(p.y, p.m);
  }, []);
  const { data: rangeData } = useCalendarRange(thisMonth.from, thisMonth.to);
  const canCreate = rangeData?.canCreate || { dept: false, company: false, companyAffiliates: [] };

  const [title, setTitle] = useState(editing?.title ?? seed.title ?? '');
  const [scope, setScope] = useState<CalendarScope>(editing?.scope ?? seed.scope ?? 'personal');
  const [companyTarget, setCompanyTarget] = useState<string>(''); // '' = 내 계열사(기본), '__all__' = 전체 공통
  const [allDay, setAllDay] = useState(init.allDay);
  const [startDate, setStartDate] = useState(init.startDate);
  const [endDate, setEndDate] = useState(init.endDate);
  const [startTime, setStartTime] = useState(init.startTime);
  const [endTime, setEndTime] = useState(init.endTime);
  const [repeatFreq, setRepeatFreq] = useState<CalendarRepeatFreq | ''>(editing?.repeat?.freq ?? '');
  const [repeatUntil, setRepeatUntil] = useState<string>(editing?.repeat?.until ?? '');
  const [remindOffset, setRemindOffset] = useState<number | null>(
    editing ? editing.remindOffset : (seed.scope === 'dept' ? null : 10),
  );
  const [location, setLocation] = useState(editing?.location ?? '');
  const [description, setDescription] = useState(editing?.description ?? seed.description ?? '');
  const [attendees, setAttendees] = useState<Array<{ userId: string; name: string }>>(
    editing ? editing.attendees.map(a => ({ userId: a.userId, name: a.name })) : (seed.attendees || []),
  );
  const [shareToRoom, setShareToRoom] = useState(!!seed.shareChannel && !editing);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const queryRef = useRef<HTMLInputElement>(null);

  const myId = String(currentUser.id);
  const { users: directory } = useAffiliateDirectory(scope === 'personal' ? currentUser.affiliateId : undefined);

  // 대화방에서 채운 참석자(seed)는 같은 계열사 명부에 있는 사람만 남긴다 — 서버는 같은 계열사 재직자만 받으므로
  // 타 계열사 멤버가 섞이면 저장이 통째로 거부된다. 수정 중인 일정의 기존 참석자는 건드리지 않는다
  const seedFiltered = useRef(false);
  useEffect(() => {
    if (seedFiltered.current || editing || !seed.attendees?.length || directory.length === 0) return;
    seedFiltered.current = true;
    const ids = new Set(directory.map(u => String(u.id)));
    setAttendees(prev => prev.filter(a => ids.has(a.userId) && a.userId !== myId));
  }, [directory, editing, seed.attendees, myId]);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const picked = new Set(attendees.map(a => a.userId));
    return directory
      .filter(u => String(u.id) !== myId && !picked.has(String(u.id)))
      .filter(u => u.username.toLowerCase().includes(q) || (u.depart || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [directory, query, attendees, myId]);

  // 시작을 옮기면 길이를 유지한 채 끝도 옮긴다
  const durationMs = useMemo(() => {
    if (allDay) return (dateKeyToMs(endDate) - dateKeyToMs(startDate));
    return toMsOf(endDate, endTime) - toMsOf(startDate, startTime);
  }, [allDay, startDate, endDate, startTime, endTime]);

  const moveStart = (nextDate: string, nextTime: string) => {
    if (!isValidDateKey(nextDate)) { setStartDate(nextDate); return; }
    const dur = Number.isFinite(durationMs) && durationMs >= 0 ? durationMs : 60 * MINUTE;
    setStartDate(nextDate);
    setStartTime(nextTime);
    if (allDay) {
      setEndDate(kstDateKey(dateKeyToMs(nextDate) + dur));
    } else {
      const endMs = toMsOf(nextDate, nextTime) + dur;
      setEndDate(kstDateKey(endMs));
      setEndTime(toTimeInput(endMs));
    }
  };

  const toggleAllDay = (next: boolean) => {
    setAllDay(next);
    if (next) {
      if (endDate < startDate) setEndDate(startDate);
      setRemindOffset(prev => (prev === null ? null : REMIND_OPTIONS_ALL_DAY.some(o => o.value === prev) ? prev : -540));
    } else {
      setRemindOffset(prev => (prev === null ? null : REMIND_OPTIONS_TIMED.some(o => o.value === prev) ? prev : 10));
      if (endDate === startDate && endTime <= startTime) {
        const endMs = toMsOf(startDate, startTime) + 60 * MINUTE;
        setEndDate(kstDateKey(endMs));
        setEndTime(toTimeInput(endMs));
      }
    }
  };

  const startDow = isValidDateKey(startDate) ? keyParts(startDate) : null;
  const repeatOptions: Array<{ value: CalendarRepeatFreq | ''; label: string }> = [
    { value: '', label: '반복 안 함' },
    { value: 'daily', label: '매일' },
    { value: 'weekly', label: startDow ? `매주 ${WEEKDAY_KO[startDow.dow]}요일` : '매주' },
    { value: 'monthly', label: startDow ? `매월 ${startDow.d}일` : '매월' },
    { value: 'yearly', label: startDow ? `매년 ${startDow.m}월 ${startDow.d}일` : '매년' },
  ];

  const scopeChoices: CalendarScope[] = editing
    ? [editing.scope]
    : (['personal', ...(canCreate.dept ? ['dept'] : []), ...(canCreate.company ? ['company'] : [])] as CalendarScope[]);
  const companyTargets = canCreate.companyAffiliates || [];

  const remindChoices = allDay ? REMIND_OPTIONS_ALL_DAY : REMIND_OPTIONS_TIMED;

  const validate = (): string | null => {
    if (!title.trim()) return '제목을 입력해 주세요.';
    if (!isValidDateKey(startDate) || !isValidDateKey(endDate)) return '날짜를 확인해 주세요.';
    if (allDay) {
      if (endDate < startDate) return '끝나는 날짜가 시작 날짜보다 빠릅니다.';
    } else if (toMsOf(endDate, endTime) <= toMsOf(startDate, startTime)) {
      return '끝나는 시간이 시작 시간보다 늦어야 합니다.';
    }
    if (repeatFreq && repeatUntil && repeatUntil < startDate) return '반복 종료일이 시작 날짜보다 빠릅니다.';
    if (repeatFreq) {
      const span = allDay ? dateKeyToMs(endDate) - dateKeyToMs(startDate) + DAY_MS : toMsOf(endDate, endTime) - toMsOf(startDate, startTime);
      if (span > 31 * DAY_MS) return '반복 일정은 한 번에 31일까지 이어질 수 있습니다.';
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { showToast({ type: 'error', message: err }); return; }
    const body: CalendarEventBody = {
      title: title.trim(),
      location: location.trim(),
      description: description.trim(),
      allDay,
      repeat: repeatFreq ? { freq: repeatFreq, until: repeatUntil || null } : null,
      remindOffset: scope === 'company' ? null : remindOffset,
      attendees: scope === 'personal' ? attendees.map(a => a.userId) : [],
    };
    if (allDay) {
      body.startDate = startDate;
      body.endDate = endDate;
    } else {
      body.start = kstIso(startDate, startTime);
      body.end = kstIso(endDate, endTime);
    }
    setSaving(true);
    try {
      if (editing) {
        body.baseUpdatedAt = editing.updatedAt;
        await calendarApi.update(editing.id, body);
        showToast({ type: 'success', message: '저장했습니다' });
      } else {
        body.scope = scope;
        if (scope === 'company' && companyTarget) body.companyAffiliateId = companyTarget === '__all__' ? null : companyTarget;
        if (shareToRoom && seed.shareChannel) body.shareChannelId = seed.shareChannel.id;
        const res = await calendarApi.create(body);
        if (res.share && !res.share.ok) {
          showToast({ type: 'error', message: `일정은 저장했지만 대화방에 공유하지 못했습니다. ${res.share.error || ''}`.trim() });
        } else {
          showToast({ type: 'success', message: res.share?.ok ? '저장하고 대화방에 공유했습니다' : '저장했습니다' });
        }
      }
      closeEditor();
    } catch (e) {
      const msg = e instanceof ApiError || e instanceof Error ? e.message : '';
      showToast({ type: 'error', message: msg || '일정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setSaving(false);
    }
  };

  const addAttendee = (u: User) => {
    if (attendees.length >= LIMIT.attendees) {
      showToast({ type: 'error', message: `참석자는 ${LIMIT.attendees}명까지 지정할 수 있습니다.` });
      return;
    }
    setAttendees(prev => [...prev, { userId: String(u.id), name: u.username }]);
    setQuery('');
    queryRef.current?.focus();
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={closeEditor}
        className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
      >
        취소
      </button>
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold disabled:opacity-50 transition-colors"
      >
        {saving ? '저장 중…' : '저장'}
      </button>
    </div>
  );

  return (
    <Modal isOpen onClose={closeEditor} title={editing ? '일정 수정' : '새 일정'} footer={footer} size="lg" closeOnOverlay={false}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <div>
          <label htmlFor="cal-title" className={labelClass}>제목</label>
          <input
            id="cal-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={LIMIT.title}
            placeholder="예: 주간 회의"
            className={inputClass}
            autoComplete="off"
          />
        </div>

        {scopeChoices.length > 1 || editing ? (
          <div>
            <span className={labelClass}>종류</span>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="일정 종류">
              {scopeChoices.map(s => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={scope === s}
                  disabled={!!editing}
                  onClick={() => {
                    setScope(s);
                    if (s === 'dept' && !editing) setRemindOffset(null);
                  }}
                  className={`h-9 px-3 rounded-lg border text-sm font-semibold flex items-center gap-1.5 transition-colors disabled:cursor-default ${
                    scope === s ? SCOPE_STYLE[s].filterOn : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span aria-hidden="true" className={`w-2 h-2 rounded-full ${SCOPE_STYLE[s].dot}`} />
                  {s === 'dept' ? (rangeData?.deptName ? `${rangeData.deptName} 일정` : '부서 일정') : s === 'company' ? '회사 일정' : '내 일정'}
                </button>
              ))}
            </div>
            {scope === 'dept' && !editing && (
              <p className="mt-1.5 text-xs text-slate-500">부서원 모두의 캘린더에 보입니다. 팀장만 등록·수정할 수 있습니다.</p>
            )}
            {scope === 'company' && !editing && companyTargets.length > 1 && (
              <select
                aria-label="회사 일정을 보여 줄 계열사"
                value={companyTarget}
                onChange={e => setCompanyTarget(e.target.value)}
                className={`${inputClass} mt-2`}
              >
                <option value="">내 계열사</option>
                {companyTargets.map(c => (
                  <option key={c.id ?? '__all__'} value={c.id ?? '__all__'}>{c.label}</option>
                ))}
              </select>
            )}
            {scope === 'company' && !editing && (
              <p className="mt-1.5 text-xs text-slate-500">계열사 직원 모두의 캘린더에 보입니다. 회사 일정은 알림을 보내지 않습니다.</p>
            )}
          </div>
        ) : null}

        <div>
          <div className="-my-2">
            <ToggleSwitch checked={allDay} onChange={toggleAllDay} label="종일" />
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-center">
            <input
              type="date"
              aria-label="시작 날짜"
              value={startDate}
              onChange={e => moveStart(e.target.value, startTime)}
              className={inputClass}
            />
            {!allDay ? (
              <input
                type="time"
                aria-label="시작 시간"
                value={startTime}
                step={300}
                onChange={e => moveStart(startDate, e.target.value)}
                className={`${inputClass} w-32`}
              />
            ) : <span />}
            <input
              type="date"
              aria-label="끝 날짜"
              value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              className={inputClass}
            />
            {!allDay ? (
              <input
                type="time"
                aria-label="끝 시간"
                value={endTime}
                step={300}
                onChange={e => setEndTime(e.target.value)}
                className={`${inputClass} w-32`}
              />
            ) : <span />}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="cal-repeat" className={labelClass}>반복</label>
            <select
              id="cal-repeat"
              value={repeatFreq}
              onChange={e => setRepeatFreq(e.target.value as CalendarRepeatFreq | '')}
              className={inputClass}
            >
              {repeatOptions.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {repeatFreq && (
            <div>
              <label htmlFor="cal-until" className={labelClass}>반복 종료일</label>
              <input
                id="cal-until"
                type="date"
                value={repeatUntil}
                min={startDate}
                onChange={e => setRepeatUntil(e.target.value)}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-slate-500">{repeatUntil ? '이 날까지 반복합니다' : '비워 두면 계속 반복합니다'}</p>
            </div>
          )}
          {scope !== 'company' && (
            <div>
              <label htmlFor="cal-remind" className={labelClass}>알림</label>
              <select
                id="cal-remind"
                value={remindOffset === null ? '' : String(remindOffset)}
                onChange={e => setRemindOffset(e.target.value === '' ? null : Number(e.target.value))}
                className={inputClass}
              >
                <option value="">알림 없음</option>
                {remindChoices.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
              </select>
              {scope === 'dept' && remindOffset !== null && (
                <p className="mt-1 text-xs text-slate-500">부서원 모두에게 알림이 갑니다</p>
              )}
            </div>
          )}
        </div>
        {repeatFreq === 'monthly' && startDow && startDow.d >= 29 && (
          <p className="-mt-2 flex items-start gap-1.5 text-xs text-slate-500">
            <Info size={13} className="shrink-0 mt-px" aria-hidden="true" />
            {startDow.d}일이 없는 달은 건너뜁니다.
          </p>
        )}
        {editing?.repeat && (
          <p className="-mt-2 flex items-start gap-1.5 text-xs text-slate-500">
            <Info size={13} className="shrink-0 mt-px" aria-hidden="true" />
            반복 일정은 모든 회차가 함께 바뀝니다.
          </p>
        )}

        <div>
          <label htmlFor="cal-location" className={labelClass}>장소</label>
          <input
            id="cal-location"
            value={location}
            onChange={e => setLocation(e.target.value)}
            maxLength={LIMIT.location}
            placeholder="예: 3층 회의실"
            className={inputClass}
            autoComplete="off"
          />
        </div>

        {scope === 'personal' && (
          <div>
            <label htmlFor="cal-attendee" className={labelClass}>
              참석자 <span className="font-normal text-slate-500">· 참석자의 캘린더에도 보이고 알림이 갑니다</span>
            </label>
            {attendees.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 mb-2">
                {attendees.map(a => (
                  <li key={a.userId} className="h-8 pl-3 pr-1 rounded-full bg-slate-100 text-sm text-slate-700 flex items-center gap-1">
                    {a.name || a.userId}
                    <button
                      type="button"
                      onClick={() => setAttendees(prev => prev.filter(x => x.userId !== a.userId))}
                      aria-label={`${a.name} 빼기`}
                      className="w-6 h-6 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200"
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
              <input
                id="cal-attendee"
                ref={queryRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (candidates[0]) addAttendee(candidates[0]);
                  }
                }}
                placeholder="동료 이름이나 부서로 찾기"
                className={`${inputClass} pl-9`}
                autoComplete="off"
              />
            </div>
            {candidates.length > 0 && (
              <ul className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm" role="listbox" aria-label="참석자 후보">
                {candidates.map(u => (
                  <li key={String(u.id)}>
                    <button
                      type="button"
                      onClick={() => addAttendee(u)}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-50"
                    >
                      <img
                        src={resolveAvatarUrl(u.avatar)} onError={handleAvatarError}
                        alt="" className="w-7 h-7 rounded-full object-cover bg-slate-100 shrink-0"
                      />
                      <span className="text-sm font-semibold text-slate-800 shrink-0">{u.username}</span>
                      <span className="text-xs text-slate-500 truncate">{u.depart || ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {query.trim() && candidates.length === 0 && (
              <p className="mt-1 text-xs text-slate-500">찾는 동료가 없습니다</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="cal-desc" className={labelClass}>설명</label>
          <textarea
            id="cal-desc"
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={LIMIT.description}
            rows={3}
            placeholder="메모를 남겨 보세요"
            className={`${inputClass} resize-y min-h-[4.5rem]`}
          />
        </div>

        {seed.shareChannel && !editing && (
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={shareToRoom}
              onChange={e => setShareToRoom(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
            />
            <Users size={15} className="text-slate-500" aria-hidden="true" />
            <span className="truncate">'{seed.shareChannel.name}' 대화방에 공유</span>
          </label>
        )}
      </form>
    </Modal>
  );
};
